/* global chrome */
import { createEmitter } from "../../Utilities/events";
import {
  CastingCapabilityState,
  CastingConnectionState,
} from "./CastingEvents";

/**
 * Load Google Cast CAF sender SDK once (browser only).
 * @returns {Promise<object>} cast.framework namespace
 */
export function loadCastFramework(options) {
  options = options || {};
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Cast requires a browser environment"));
  }
  if (window.cast && window.cast.framework) {
    return Promise.resolve(window.cast.framework);
  }

  return new Promise(function (resolve, reject) {
    var existing = document.querySelector("script[data-ngp-cast-sdk]");
    var timeoutMs = options.timeoutMs != null ? options.timeoutMs : 12000;

    var timer = setTimeout(function () {
      reject(new Error("Cast SDK load timeout"));
    }, timeoutMs);

    function onReady() {
      clearTimeout(timer);
      if (window.cast && window.cast.framework) {
        resolve(window.cast.framework);
      } else {
        reject(new Error("Cast framework missing after SDK load"));
      }
    }

    window["__onGCastApiAvailable"] = function (isAvailable) {
      if (isAvailable) {
        onReady();
      } else {
        clearTimeout(timer);
        reject(new Error("Google Cast API unavailable"));
      }
    };

    if (existing) {
      if (window.cast && window.cast.framework) {
        onReady();
      }
      return;
    }

    var script = document.createElement("script");
    script.src =
      options.sdkUrl ||
      "https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1";
    script.async = true;
    script.setAttribute("data-ngp-cast-sdk", "1");
    script.onerror = function () {
      clearTimeout(timer);
      reject(new Error("Failed to load Cast sender SDK"));
    };
    document.head.appendChild(script);
  });
}

/**
 * Chromecast CastingProvider — SDK isolated here; PlaybackEngine never imports Cast.
 *
 * @param {object} [options]
 * @param {string} [options.receiverApplicationId] white-label receiver app id
 * @param {boolean} [options.autoJoinPolicy]
 */
export function createChromecastProvider(options) {
  options = options || {};
  var emitter = createEmitter();
  var state = CastingConnectionState.DISCONNECTED;
  var available = false;
  var framework = null;
  var context = null;
  var remotePlayer = null;
  var remoteController = null;
  var devices = [];
  var session = null;
  var syncTimer = null;
  var lastError = null;
  var receiverAppId =
    options.receiverApplicationId ||
    (typeof chrome !== "undefined" &&
    chrome.cast &&
    chrome.cast.media &&
    chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID) ||
    "CC1AD845";

  function setState(next) {
    if (state === next) return;
    state = next;
    emitter.emit("connectionState", state);
  }

  function capabilities() {
    return {
      id: "chromecast",
      name: "Chromecast",
      discover: true,
      play: true,
      pause: true,
      seek: true,
      stop: true,
      volume: true,
      mute: true,
      playbackRate: false,
      textTracks: true,
      audioTracks: false,
      reconnect: true,
      operationalOnWeb: true,
    };
  }

  function capabilityState() {
    if (state === CastingConnectionState.CONNECTING) {
      return CastingCapabilityState.CONNECTING;
    }
    if (state === CastingConnectionState.ERROR) {
      return CastingCapabilityState.ERROR;
    }
    if (state === CastingConnectionState.CONNECTED) {
      var remote = getRemoteState();
      if (remote && remote.playing) {
        return CastingCapabilityState.PLAYING;
      }
      if (remote && remote.mediaInfo) {
        return CastingCapabilityState.PAUSED;
      }
      return CastingCapabilityState.CONNECTED;
    }
    if (available) {
      return CastingCapabilityState.AVAILABLE;
    }
    return CastingCapabilityState.DISCONNECTED;
  }

  function refreshDevices() {
    devices = [];
    if (context && context.getCurrentSession && context.getCurrentSession()) {
      var s = context.getCurrentSession();
      var name =
        (s.getCastDevice && s.getCastDevice() && s.getCastDevice().friendlyName) ||
        "Chromecast";
      devices = [
        {
          id: "chromecast-active",
          name: name,
          provider: "chromecast",
          type: "chromecast",
          connected: state === CastingConnectionState.CONNECTED,
          discoveryMode: "session",
        },
      ];
    } else if (available) {
      // CAF does not expose a named device list before the system picker.
      // Signal real Cast availability without inventing a fake TV name.
      devices = [
        {
          id: "chromecast-picker",
          name: "Chromecast / Google Cast (system picker)",
          provider: "chromecast",
          type: "chromecast",
          connected: false,
          discoveryMode: "system_picker",
        },
      ];
    }
    emitter.emit("devices", devices.slice());
  }

  function stopSync() {
    if (syncTimer) {
      clearInterval(syncTimer);
      syncTimer = null;
    }
  }

  function startSync() {
    stopSync();
    syncTimer = setInterval(function () {
      emitter.emit("remoteState", getRemoteState());
    }, 1000);
  }

  function bindRemotePlayer() {
    if (!framework) return;
    remotePlayer = new framework.RemotePlayer();
    remoteController = new framework.RemotePlayerController(remotePlayer);
    remoteController.addEventListener(
      framework.RemotePlayerEventType.ANY_CHANGE,
      function () {
        emitter.emit("remoteState", getRemoteState());
      }
    );
  }

  function getRemoteState() {
    if (!remotePlayer || state !== CastingConnectionState.CONNECTED) {
      return {
        target: "local",
        playing: false,
        currentTime: 0,
        duration: 0,
        volume: 1,
        muted: false,
        playbackRate: 1,
      };
    }
    return {
      target: "remote",
      playing: !remotePlayer.isPaused && remotePlayer.isConnected,
      currentTime: remotePlayer.currentTime || 0,
      duration: remotePlayer.duration || 0,
      volume: remotePlayer.volumeLevel != null ? remotePlayer.volumeLevel : 1,
      muted: !!remotePlayer.isMuted,
      playbackRate: 1,
      mediaInfo: remotePlayer.mediaInfo || null,
    };
  }

  function onSessionState(event) {
    if (!framework) return;
    var SessionState = framework.SessionState;
    switch (event.sessionState) {
      case SessionState.SESSION_STARTED:
      case SessionState.SESSION_RESUMED:
        session = context.getCurrentSession();
        setState(CastingConnectionState.CONNECTED);
        bindRemotePlayer();
        startSync();
        refreshDevices();
        emitter.emit("session", { type: "started", session: session });
        break;
      case SessionState.SESSION_ENDED:
        session = null;
        stopSync();
        setState(CastingConnectionState.DISCONNECTED);
        refreshDevices();
        emitter.emit("session", { type: "ended", reason: event.opt_errorCode });
        break;
      default:
        break;
    }
  }

  return {
    name: "chromecast",
    getCapabilities: capabilities,
    initialize: function () {
      var self = this;
      return loadCastFramework(options)
        .then(function (fw) {
          framework = fw;
          context = fw.CastContext.getInstance();
          var AutoJoin =
            (window.chrome &&
              chrome.cast &&
              chrome.cast.AutoJoinPolicy &&
              chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED) ||
            "origin_scoped";
          context.setOptions({
            receiverApplicationId: receiverAppId,
            autoJoinPolicy: options.autoJoinPolicy || AutoJoin,
            resumeSavedSession: options.resumeSavedSession !== false,
          });
          context.addEventListener(
            fw.CastContextEventType.CAST_STATE_CHANGED,
            function (e) {
              available =
                e.castState === fw.CastState.NOT_CONNECTED ||
                e.castState === fw.CastState.CONNECTED ||
                e.castState === fw.CastState.CONNECTING;
              if (e.castState === fw.CastState.CONNECTING) {
                setState(CastingConnectionState.CONNECTING);
              }
              if (e.castState === fw.CastState.NO_DEVICES_AVAILABLE) {
                available = false;
              }
              emitter.emit("availability", available);
              refreshDevices();
            }
          );
          context.addEventListener(
            fw.CastContextEventType.SESSION_STATE_CHANGED,
            onSessionState
          );
          available = context.getCastState() !== fw.CastState.NO_DEVICES_AVAILABLE;
          emitter.emit("availability", available);
          refreshDevices();
        })
        .catch(function (err) {
          lastError = err;
          available = false;
          emitter.emit("availability", false);
          emitter.emit("error", {
            code: "CAST_INIT_FAILED",
            message: (err && err.message) || "Chromecast init failed",
            recoverable: true,
          });
          // Soft-fail: provider remains registered but unavailable
          return Promise.resolve();
        });
    },
    isAvailable: function () {
      return available;
    },
    isConnected: function () {
      return state === CastingConnectionState.CONNECTED;
    },
    getCapabilityState: capabilityState,
    discoverDevices: function () {
      refreshDevices();
      return Promise.resolve(devices.slice());
    },
    getDevices: function () {
      return devices.slice();
    },
    connect: function (deviceId) {
      if (!context) {
        return Promise.reject(new Error("Chromecast not initialized"));
      }
      setState(CastingConnectionState.CONNECTING);
      emitter.emit("session", { type: "starting", deviceId: deviceId });
      return context
        .requestSession()
        .then(function () {
          setState(CastingConnectionState.CONNECTED);
          session = context.getCurrentSession();
          bindRemotePlayer();
          startSync();
          refreshDevices();
          emitter.emit("session", { type: "started" });
        })
        .catch(function (err) {
          setState(CastingConnectionState.ERROR);
          lastError = err;
          emitter.emit("error", {
            code: "CAST_CONNECT_FAILED",
            message: (err && err.message) || "Cast connect failed",
            recoverable: true,
          });
          setState(CastingConnectionState.DISCONNECTED);
          return Promise.reject(err);
        });
    },
    disconnect: function () {
      stopSync();
      if (context && context.getCurrentSession()) {
        try {
          context.endCurrentSession(true);
        } catch (e) {
          /* ignore */
        }
      }
      session = null;
      setState(CastingConnectionState.DISCONNECTED);
      refreshDevices();
      emitter.emit("session", { type: "ended", reason: "user" });
      return Promise.resolve();
    },
    /**
     * @param {object} media { src, contentType, title, poster, currentTime, autoplay, textTrack?, audioTrack? }
     */
    loadMedia: function (media) {
      media = media || {};
      if (!session && context) {
        session = context.getCurrentSession();
      }
      if (!session) {
        return Promise.reject(new Error("No Chromecast session"));
      }
      var contentType =
        media.contentType ||
        (media.src && media.src.indexOf(".m3u8") !== -1
          ? "application/x-mpegURL"
          : "video/mp4");
      var mediaInfo = new chrome.cast.media.MediaInfo(media.src, contentType);
      mediaInfo.metadata = new chrome.cast.media.GenericMediaMetadata();
      mediaInfo.metadata.title = media.title || "Video";
      if (media.poster) {
        mediaInfo.metadata.images = [{ url: media.poster }];
      }
      if (media.textTrack && media.textTrack.src) {
        var track = new chrome.cast.media.Track(1, chrome.cast.media.TrackType.TEXT);
        track.trackContentId = media.textTrack.src;
        track.trackContentType = "text/vtt";
        track.name = media.textTrack.label || "Subtitles";
        track.language = media.textTrack.language || "en";
        track.subtype = chrome.cast.media.TextTrackType.SUBTITLES;
        mediaInfo.tracks = [track];
      }
      var request = new chrome.cast.media.LoadRequest(mediaInfo);
      request.currentTime = media.currentTime || 0;
      request.autoplay = media.autoplay !== false;
      if (media.textTrack && mediaInfo.tracks) {
        request.activeTrackIds = [1];
      }
      return session.loadMedia(request).then(function () {
        bindRemotePlayer();
        startSync();
        emitter.emit("remoteState", getRemoteState());
      });
    },
    play: function () {
      if (remoteController && remotePlayer && remotePlayer.isPaused) {
        remoteController.playOrPause();
      }
      return Promise.resolve();
    },
    pause: function () {
      if (remoteController && remotePlayer && !remotePlayer.isPaused) {
        remoteController.playOrPause();
      }
      return Promise.resolve();
    },
    seek: function (time) {
      if (remotePlayer && remoteController) {
        remotePlayer.currentTime = time;
        remoteController.seek();
      }
      return Promise.resolve();
    },
    stop: function () {
      if (remoteController) {
        remoteController.stop();
      }
      return Promise.resolve();
    },
    setVolume: function (level) {
      if (remotePlayer && remoteController) {
        remotePlayer.volumeLevel = Math.max(0, Math.min(1, level));
        remoteController.setVolumeLevel();
      }
      return Promise.resolve();
    },
    setMuted: function (muted) {
      if (remotePlayer && remoteController) {
        if (!!muted !== !!remotePlayer.isMuted) {
          remoteController.muteOrUnmute();
        }
      }
      return Promise.resolve();
    },
    synchronizeState: function (localSnapshot) {
      // Push local→remote when starting cast; ongoing sync is remote→UI via remoteState
      if (!localSnapshot || state !== CastingConnectionState.CONNECTED) {
        return Promise.resolve(getRemoteState());
      }
      var tasks = [];
      if (localSnapshot.currentTime != null) {
        tasks.push(this.seek(localSnapshot.currentTime));
      }
      if (localSnapshot.muted != null) {
        tasks.push(this.setMuted(localSnapshot.muted));
      }
      if (localSnapshot.volume != null) {
        tasks.push(this.setVolume(localSnapshot.volume));
      }
      if (localSnapshot.playing) {
        tasks.push(this.play());
      } else {
        tasks.push(this.pause());
      }
      return Promise.all(tasks).then(function () {
        return getRemoteState();
      });
    },
    getRemoteState: getRemoteState,
    getConnectionState: function () {
      return state;
    },
    getLastError: function () {
      return lastError;
    },
    on: emitter.on,
    off: emitter.off,
    destroy: function () {
      stopSync();
      try {
        if (context && context.getCurrentSession()) {
          context.endCurrentSession(true);
        }
      } catch (e) {
        /* ignore */
      }
      emitter.removeAllListeners();
      setState(CastingConnectionState.DISCONNECTED);
    },
  };
}

export default createChromecastProvider;
